/**
 * Utilitaires du wizard d'import établissement (moteur POST /eleves/reconcilier/).
 *
 * Règle d'or (SPEC_GAR_FICHE_UNIQUE §10) : les valeurs partent BRUTES au back —
 * l'unique normalise() vit côté serveur, aucune transformation ici.
 *
 * Contrat : DEUX formats exclusifs, jamais mélangés (décision 2026-09-01) :
 *   - mode A (prioritaire) : Nom + Prénom + Classe (le modèle téléchargeable) ;
 *   - mode B (tolérance Pronote) : Élèves + Classe, la colonne « Élèves »
 *     (« NOM Prénom ») étant DÉCOUPÉE par la casse (decouperNomPrenom).
 * La réconciliation se fait par nom. Toute autre colonne d'un export
 * Pronote/SIECLE (INE, date de naissance, sexe...) est simplement ignorée :
 * rien d'autre ne part au back, qui tolère et ignore les vieux payloads plus
 * larges.
 */

import { findClasseMatch, detectSeparator, parseCSVLine } from './importElevesUtils';

// ─── Détection tolérante des 3 colonnes obligatoires ─────────────────────────

// Diacritiques combinants U+0300–U+036F (regex construite par char codes pour
// éviter tout souci d'échappement d'éditeur)
const DIACRITIQUES = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

const normaliserEntete = (h) =>
    String(h || '')
        .normalize('NFD')
        .replace(DIACRITIQUES, '')
        .toLowerCase()
        .replace(/[^a-z()]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export const COLONNES_IMPORT = [
    {
        cle: 'nom',
        libelle: 'Nom',
        // « Nom de famille » : libellé SIECLE.
        correspond: (h) => h === 'nom' || h.startsWith('nom de l') || h === 'nom eleve' || h === 'nom de famille',
    },
    {
        cle: 'prenom',
        libelle: 'Prénom',
        // « Prénom 1 » : libellé SIECLE (normalisé sans le chiffre : 'prenom').
        correspond: (h) => h === 'prenom' || h.startsWith('prenom de l') || h === 'prenom eleve',
    },
    {
        cle: 'classe',
        libelle: 'Classe',
        // « Division » : libellé SIECLE.
        correspond: (h) => h === 'classe' || h === 'division',
    },
];

/** Mode B : la colonne unique « Élèves » des exports Pronote (« NOM Prénom »).
 *  Matcher aligné sur CollerNotes (collerNotes.js) MOINS 'nom'/'noms' :
 *  « Nom » seul reste une colonne du mode A. */
export const COLONNE_ELEVES = {
    libelle: 'Élèves',
    correspond: (h) => h === 'eleve' || h === 'eleves',
};

/**
 * Découpe « NOM Prénom » (format Pronote) par la CASSE, pas par l'espace :
 * les tokens entièrement en majuscules en tête forment le nom (particules
 * « DE LA », tirets, apostrophes inclus), tout le reste le prénom
 * (« DE LA FONTAINE Jean-Pierre » → nom « DE LA FONTAINE »).
 * Replis quand la casse ne tranche pas :
 *   - tout en majuscules (« DUPONT MARIE ») : dernier token = prénom, le
 *     reste = nom (le moins faux ; le dry-run du wizard fait le filet) ;
 *   - un seul token : nom = token, prénom vide.
 * Les morceaux restent BRUTS (trim seulement) : découper n'est pas
 * normaliser.
 */
export const decouperNomPrenom = (brut) => {
    const tokens = String(brut || '').trim().split(/\s+/).filter((t) => t !== '');
    if (tokens.length === 0) return { nom: '', prenom: '' };
    if (tokens.length === 1) return { nom: tokens[0], prenom: '' };
    const estMajuscule = (t) => /[a-zA-ZÀ-ſ]/.test(t) && t.toLocaleUpperCase('fr') === t;
    let frontiere = 0;
    while (frontiere < tokens.length && estMajuscule(tokens[frontiere])) frontiere++;
    // Tout majuscule (aucune frontière) → repli dernier token = prénom.
    if (frontiere === tokens.length) frontiere = tokens.length - 1;
    // Aucun token majuscule en tête (fichier recapitalisé) → repli inverse :
    // premier token = nom (Pronote met toujours le nom d'abord).
    if (frontiere === 0) frontiere = 1;
    return {
        nom: tokens.slice(0, frontiere).join(' '),
        prenom: tokens.slice(frontiere).join(' '),
    };
};

/**
 * Extrait les lignes {nom, prenom, classe} de toutes les feuilles lues par
 * readExcel (une feuille = un array d'objets clés par en-têtes). Valeurs
 * BRUTES (trim uniquement). Les colonnes surnuméraires d'un export
 * Pronote/SIECLE (INE, Date de naissance, Sexe...) sont simplement ignorées.
 *
 * Deux formats par feuille, EXCLUSIFS (jamais mélangés) :
 *   - mode A (prioritaire) : Nom + Prénom + Classe — si les trois sont là,
 *     une éventuelle colonne « Élèves » est ignorée ;
 *   - mode B : Élèves + Classe — « NOM Prénom » découpé par la casse.
 *
 * Une ligne SANS CLASSE (nom ou prénom, colonne Classe vide) est IGNORÉE et
 * comptée dans `sansClasse` : on n'importe que les élèves qui ont une classe
 * (décision prof 2026-09-12 ; avant, elles formaient un groupe « nouvelle »
 * au nom vide à l'étape Classes, que personne ne comprenait).
 *
 * @returns {{ lignes: Array, sansClasse: number, erreur: string|null }} erreur
 * non nulle si aucune feuille ne porte l'un des deux formats (formats attendus
 * + colonnes trouvées — refus UX immédiat, le back re-valide de toute façon).
 */
export const parserLignesImport = (sheetsData) => {
    const lignes = [];
    let sansClasse = 0;
    let derniereAnalyse = null;

    (sheetsData || []).forEach((feuille) => {
        if (!Array.isArray(feuille) || feuille.length === 0) return;
        const entetes = Object.keys(feuille[0]);
        const mapping = {};
        COLONNES_IMPORT.forEach((col) => {
            const trouvee = entetes.find((e) => col.correspond(normaliserEntete(e)));
            if (trouvee !== undefined) mapping[col.cle] = trouvee;
        });
        const colEleves = entetes.find((e) => COLONNE_ELEVES.correspond(normaliserEntete(e)));
        const modeA = mapping.nom !== undefined && mapping.prenom !== undefined && mapping.classe !== undefined;
        const modeB = !modeA && colEleves !== undefined && mapping.classe !== undefined;
        derniereAnalyse = { entetes };
        if (!modeA && !modeB) return; // feuille inutilisable (ex. feuille de garde)

        feuille.forEach((row) => {
            const brut = (colonne) => {
                const v = row[colonne];
                return String(v === null || v === undefined ? '' : v).trim();
            };
            const ligne = modeA
                ? {
                    nom: brut(mapping.nom),
                    prenom: brut(mapping.prenom),
                    classe: brut(mapping.classe),
                }
                : {
                    ...decouperNomPrenom(brut(colEleves)),
                    classe: brut(mapping.classe),
                };
            // Ignorer les lignes entièrement vides (queues de fichier Excel)
            if (!ligne.nom && !ligne.prenom && !ligne.classe) return;
            if (!ligne.classe) { sansClasse += 1; return; }
            lignes.push(ligne);
        });
    });

    if (lignes.length === 0) {
        if (sansClasse > 0) {
            return {
                lignes: [],
                sansClasse,
                erreur: `Aucun élève n'a de classe dans votre fichier (${sansClasse} ligne${sansClasse > 1 ? 's' : ''} sans classe). `
                    + 'Remplissez la colonne Classe puis réimportez.',
            };
        }
        if (derniereAnalyse) {
            return {
                lignes: [],
                sansClasse,
                erreur: 'Colonnes attendues : « Nom, Prénom, Classe » ou bien « Élèves, Classe » (export Pronote). '
                    + `Colonnes trouvées dans le fichier : ${derniereAnalyse.entetes.join(', ')}. `
                    + 'Téléchargez le modèle pour voir le format exact.',
            };
        }
        return { lignes: [], sansClasse, erreur: "Le fichier est vide ou ne contient aucune ligne exploitable." };
    }

    return { lignes, sansClasse, erreur: null };
};

/**
 * Variante presse-papier : transforme le texte collé (copie Excel/Pronote,
 * séparateur tab/;/,) en « feuille » d'objets puis passe au même parseur —
 * une seule chaîne de validation pour les deux entrées.
 */
export const parserPressePapier = (texte) => {
    const lignesTexte = String(texte || '').split('\n').filter((l) => l.trim() !== '');
    if (lignesTexte.length < 2) {
        return { lignes: [], erreur: "Le presse-papier ne contient pas assez de données (en-têtes + élèves)." };
    }
    const separateur = detectSeparator(lignesTexte[0]);
    if (!separateur) {
        return { lignes: [], erreur: "Impossible de détecter le séparateur. Copiez directement depuis Excel ou Pronote." };
    }
    const entetes = parseCSVLine(lignesTexte[0], separateur);
    const rows = lignesTexte.slice(1).map((l) => {
        const valeurs = parseCSVLine(l, separateur);
        const row = {};
        entetes.forEach((e, i) => { row[e] = valeurs[i] !== undefined ? valeurs[i] : ''; });
        return row;
    });
    return parserLignesImport([rows]);
};

/**
 * Regroupe les lignes par classe et rapproche chaque classe du référentiel
 * TanoEd (findClasseMatch : accents/casse/espaces ignorés + startsWith pour la
 * troncature Excel « 501 » → « 501 LCE ULIS »).
 *
 * @param {Array} lignes - lignes parsées
 * @param {Array} listClasses - classes TanoEd [{id, nom, nombre_eleves, ...}]
 * @returns {Array<{cle, nomFichier, lignes, nomReconnu, classeId, nombreActuel}>}
 */
export const grouperParClasses = (lignes, listClasses) => {
    const parCle = new Map();
    lignes.forEach((l) => {
        const cle = l.classe.toUpperCase();
        if (!parCle.has(cle)) parCle.set(cle, { cle, nomFichier: l.classe, lignes: [] });
        parCle.get(cle).lignes.push(l);
    });
    return [...parCle.values()]
        .map((g) => {
            const match = findClasseMatch(g.cle, listClasses || []);
            return {
                ...g,
                nomReconnu: match ? match.nom : null,
                classeId: match ? match.id : null,
                nombreActuel: match ? (match.nombre_eleves || 0) : 0,
            };
        })
        .sort((a, b) => a.nomFichier.localeCompare(b.nomFichier, 'fr'));
};

/**
 * Lignes à envoyer au moteur : classes cochées uniquement, avec `classe`
 * alignée sur le nom TanoEd quand la classe est reconnue (spec §10.0 —
 * fiabilise la comparaison de classes du moteur et l'auto-liaison GAR).
 */
export const lignesPourMoteur = (groupes, cochees) =>
    groupes
        .filter((g) => cochees.has(g.cle))
        .flatMap((g) => g.lignes.map((l) => ({ ...l, classe: g.nomReconnu || g.nomFichier })));

/** Effectif fichier total des classes cochées (compteur licence, spec §10.1bis). */
export const sommeEffectifsCoches = (groupes, cochees) =>
    groupes.reduce((acc, g) => (cochees.has(g.cle) ? acc + g.lignes.length : acc), 0);

/**
 * Classes TanoEd PEUPLÉES absentes du fichier : leurs élèves passeront
 * sortants à l'apply (« absent du fichier = sortant »). À montrer dès l'étape
 * classes pour ne pas découvrir la casse au dry-run.
 */
export const classesEnBaseHorsFichier = (groupes, listClasses) => {
    const idsReconnus = new Set(groupes.map((g) => g.classeId).filter((id) => id !== null));
    return (listClasses || []).filter((c) => (c.nombre_eleves || 0) > 0 && !idsReconnus.has(c.id));
};
